import { isNil } from 'lodash-es';
import { Resource } from '../Resource';
import { OptionCode } from '../enums';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { deviceMutationExternalInvalidationKeys } from './devices';
const rootPath = 'stations';
export const stationCompatibleOptions = [OptionCode.weephyt, OptionCode.cumulativeSum];
const toRaw = {
    create: (payload) => ({
        organization: payload.organizationId,
        org_device: payload.deviceId,
        name: payload.name
    }),
    changeFavorite: (payload) => ({
        favorite: payload.isFavorite
    }),
    update: (payload) => ({
        favorite: payload.isFavorite,
        name: payload.name
    })
};
export const fromRawStationToStation = (rawStation) => ({
    kind: 'station',
    id: rawStation.id,
    deviceId: rawStation.org_device,
    name: rawStation.name ?? rawStation.location_text,
    isDynamicName: isNil(rawStation.name),
    isFavorite: rawStation.favorite,
    createdAt: rawStation.created_at,
    brand: rawStation.brand,
    serial: rawStation.model,
    model: rawStation.model,
    availableMeasures: rawStation.available_measures,
    organizationId: rawStation.organization,
    origin: rawStation.origin,
    networkId: rawStation.network,
    timezone: rawStation.tzinfo,
    lastMeasuredAt: rawStation.last_measured_at,
    lastLocatedAt: rawStation.last_located_at,
    endUserName: rawStation.enduser_name,
    plotIds: rawStation.plots,
    location: rawStation.location,
    reverseGeocoding: rawStation.location_text,
    currentEvents: rawStation.current_events,
    messageQuality: rawStation.message_quality,
    currentMessageQuality: rawStation.current_message_quality,
    activeOptions: rawStation.active_options,
    broadcastNetwork: rawStation.broadcast_network_provider,
    rotronicProbeChange: rawStation.rotronic_probe_change
});
export const stationsMutationExternalInvalidationKeys = new Set();
const stations = Resource((requesterBuilder) => {
    const get = methods.get({
        rootPath,
        fromRaw: fromRawStationToStation,
        requesterBuilder
    });
    const createGetMany = () => (query) => {
        const newRequester = requesterBuilder({
            path: rootPath,
            requestCtx: {
                transform: transformers.paginatedResponse(fromRawStationToStation),
                params: {
                    query: {
                        available_options: query?.availableOptions,
                        limit: query?.limit,
                        universe: query?.universe,
                        offset: query?.offset,
                        id__in: !isNil(query?.ids) ? query?.ids.join(',') : undefined,
                        metrics__in: query?.metrics,
                        metrics_op: query?.metricOperator,
                        q: query?.q,
                        organization: query?.organizationId,
                        virtual_device: query?.hasVirtualDevice
                    }
                }
            }
        });
        deviceMutationExternalInvalidationKeys.add(newRequester.invalidationKey[0]);
        return newRequester;
    };
    const getMany = createGetMany();
    const buildKeysToInvalidateOnCreation = () => [
        ...Array.from(stationsMutationExternalInvalidationKeys).map((k) => [k]),
        getMany().invalidationKey
    ];
    const buildKeysToInvalidateOnDeletion = (id) => [
        ...Array.from(stationsMutationExternalInvalidationKeys).map((k) => [k]),
        get(id).key,
        getMany().invalidationKey
    ];
    return {
        get,
        getMany,
        getPage: methods.getPage({ getMany }),
        getAll: methods.getAllPages({ getMany }),
        create: methods.create({
            rootPath,
            fromRaw: fromRawStationToStation,
            requesterBuilder,
            toRaw: (payload) => toRaw.create(payload),
            buildKeysToInvalidate: buildKeysToInvalidateOnCreation
        }),
        changeFavorite: methods.update({
            rootPath,
            method: 'PATCH',
            fromRaw: fromRawStationToStation,
            requesterBuilder,
            toRaw: (payload) => toRaw.changeFavorite(payload),
            buildKeysToInvalidate: buildKeysToInvalidateOnDeletion
        }),
        delete: methods.deletion({
            requesterBuilder,
            rootPath,
            buildKeysToInvalidate: buildKeysToInvalidateOnDeletion
        }),
        update: methods.update({
            rootPath,
            method: 'PATCH',
            fromRaw: fromRawStationToStation,
            requesterBuilder,
            toRaw: (payload) => toRaw.update(payload),
            buildKeysToInvalidate: buildKeysToInvalidateOnDeletion
        })
    };
});
export default stations;
